import React, { useState, useEffect } from "react";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Mail,
  MapPin,
  Phone,
  Calendar,
  Globe,
  CheckCircle,
  XCircle,
  Download,
  Eye,
  Upload,
  Plus,
  Edit,
  Trash2,
  X,
  ChevronLeft,
  ChevronRight,
  User,
  Receipt,
  ClipboardList,
  Crown,
  FileText,
  Search,
  Lock,
} from "lucide-react";
import PopupTasks from "../../TaskAdmin/PopupTasks";
import {
  AdminPopup,
  AdminLayout,
  SideNav,
  CollapseButton,
  ConfirmDialog,
  Avatar,
  StatusBadge,
  Pill,
  Empty,
  Skeleton,
  useSidebarCollapsed,
  formatDate,
  todayISO,
} from "../../../dashboardAdmin/sidebar/adminLayout" 
import "./profile.css";

/* ============================================================
   Configuration
   ============================================================ */
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const NAV_GROUPS = [
  { label: "Account", items: [["details", "Info", User]] },
  { label: "Billing", items: [["invoices", "Invoices", Receipt]] },
  { label: "Work", items: [["tasks", "Tasks", ClipboardList]] },
  {
    label: "VIP",
    items: [
      ["facilityVip", "FacilityVip", Crown],
      ["reports", "Reports", FileText],
    ],
  },
];

const TAB_TITLES = {
  details: "Personal information",
  invoices: "Invoices",
  tasks: "Tasks",
  facilityVip: "FacilityVip",
  reports: "Reports",
};

const emptyVip = { totalPoints: "0", availablePoints: "0", BlockedPoints: "0" };
const emptyForm = () => ({ file: null, date: todayISO(), status: "pending" });

/* ============================================================
   Petits composants de la page
   ============================================================ */
const InfoField = ({ icon: Icon, label, value }) => (
  <div className="adm-field">
    <Icon />
    <div className="adm-field-text">
      <span className="adm-field-label">{label}</span>
      <span className="adm-field-value">{value || "N/A"}</span>
    </div>
  </div>
);

/* Liste simple de documents (rapports) */
const DocsPanel = ({ title, itemLabel, items, onAdd, onView, onDelete, emptyText }) => (
  <>
    <div className="adm-doc-toolbar">
      <h5>{title}</h5>
      <button className="adm-btn primary" onClick={onAdd}>
        <Plus /> Add
      </button>
    </div>
    {items.length === 0 ? (
      <Empty>{emptyText}</Empty>
    ) : (
      items.map((it, i) => (
        <div key={it._id || i} className="adm-doc">
          <div className="adm-doc-icon">
            <FileText size={18} />
          </div>
          <div className="adm-doc-text">
            <div className="adm-doc-title">
              {itemLabel} {i + 1}
            </div>
            <div className="adm-doc-date">{formatDate(it.createdAt)}</div>
          </div>
          <button className="adm-icon-btn" title="View" onClick={() => onView(it._id)}>
            <Eye />
          </button>
          <button className="adm-icon-btn danger" title="Delete" onClick={() => onDelete(it._id)}>
            <Trash2 />
          </button>
        </div>
      ))
    )}
  </>
);

/* Champ fichier PDF avec aperçu du nom */
const FileInput = ({ file, setFile, disabled }) => (
  <>
    <label>
      <span>PDF file</span>
      <input
        type="file"
        accept=".pdf"
        disabled={disabled}
        onChange={(e) => setFile(e.target.files[0] || null)}
      />
    </label>
    {file && (
      <div className="adm-file-preview">
        <p>
          Selected: <strong>{file.name}</strong>
        </p>
      </div>
    )}
  </>
);

/* Carte de formulaire : remplace le contenu de l'onglet puis revient après envoi ou "Cancel" */
const FormCard = ({ title, children, submitLabel, onSubmit, onCancel, busy, disabled }) => (
  <div className="adm-upload-card">
    <div className="adm-doc-toolbar" style={{ marginBottom: 14 }}>
      <h5 style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Upload size={16} /> {title}
      </h5>
    </div>
    <div className="adm-form">
      {children}
      <div className="adm-upload-buttons">
        <button className="adm-upload-btn" onClick={onSubmit} disabled={busy || disabled}>
          {busy ? "Saving..." : submitLabel}
        </button>
        <button className="adm-cancel-btn" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </div>
  </div>
);

/* ============================================================
   Composant principal
   ============================================================ */
const Profile = ({ onClose }) => {
  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
  const navigate = useNavigate();
  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

  const [collapsed, toggleCollapsed] = useSidebarCollapsed();

  /* Session */
  const [userRole, setUserRole] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);

  /* Liste + sélection */
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("all");
  const [selectedUser, setSelectedUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [viewMode, setViewMode] = useState("details");

  /* Chargements */
  const [isLoading, setIsLoading] = useState(true); // liste des utilisateurs
  const [loadError, setLoadError] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [tabLoading, setTabLoading] = useState(false);

  /* Données de l'utilisateur sélectionné */
  const [invoices, setInvoices] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [facilityVip, setFacilityVip] = useState(null);
  const [reports, setReports] = useState([]);

  /* Interface */
  const [filterMonth, setFilterMonth] = useState("");
  const [filterYear, setFilterYear] = useState("");
  const [confirm, setConfirm] = useState(null);

  /* Formulaires (création / modification) */
  const [formType, setFormType] = useState(null); // invoice | task | facilityVip | report
  const [form, setForm] = useState(emptyForm());
  const [vipForm, setVipForm] = useState(emptyVip);
  const [editInvoiceId, setEditInvoiceId] = useState(null);
  const [editingVip, setEditingVip] = useState(false);
  const [saving, setSaving] = useState(false);

  const isCommercial = userRole === "commercial_admin";

  /* ---------- Session : rôle et id depuis le token ---------- */
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("No authentication token found. Please log in again.");
      navigate("/login");
      return;
    }
    try {
      const decoded = jwtDecode(token);
      const currentTime = Math.floor(Date.now() / 1000);
      if (decoded.exp < currentTime) {
        toast.error("Token expired. Please log in again.");
        localStorage.removeItem("token");
        localStorage.removeItem("role");
        navigate("/login");
        return;
      }
      if (decoded.role) {
        setUserRole(decoded.role);
        setCurrentUserId(decoded.id);
        localStorage.setItem("role", decoded.role);
      } else {
        toast.error("Role not found in token. Please log in again.");
        navigate("/login");
      }
    } catch (error) {
      console.error("Error decoding token:", error);
      toast.error("Invalid token. Please log in again.");
      navigate("/login");
    }
  }, [navigate]);

  /* ---------- Liste des utilisateurs ---------- */
  useEffect(() => {
    if (!userRole) return;
    const fetchUsers = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(`${API_URL}/profiles/users-with-status`, {
          headers: authHeaders(),
        });
        const list =
          userRole === "commercial_admin"
            ? (response.data || []).filter((user) => user.isVerified)
            : response.data || [];
        setUsers(list);
        setLoadError(null);
      } catch (error) {
        console.error("Error fetching users:", error.response?.data || error.message);
        setLoadError(error.response?.data?.message || "Failed to fetch users.");
        setUsers([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRole]);

  /* ---------- Profil de l'utilisateur sélectionné ---------- */
  useEffect(() => {
    if (!selectedUser?._id) return;
    let cancelled = false;

    const fetchProfile = async () => {
      setProfileLoading(true);
      try {
        const response = await axios.get(`${API_URL}/profiles/${selectedUser._id}`, {
          headers: authHeaders(),
        });
        if (!cancelled) setProfile(response.data || null);
      } catch (error) {
        console.error("Error fetching profile:", error.response?.data || error.message);
        if (!cancelled) toast.error(error.response?.data?.message || "Failed to fetch profile details.");
      } finally {
        if (!cancelled) setProfileLoading(false);
      }
    };
    fetchProfile();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUser]);

  /* ---------- Préchargement de tous les onglets (alimente les compteurs) ---------- */
  useEffect(() => {
    if (!selectedUser?._id) return;
    const userId = selectedUser._id;

    /* Même règle qu'avant : un admin / commercial voit tout, les autres seulement leurs données */
    const allowed = ["admin", "commercial_admin"].includes(userRole) || currentUserId === userId;
    if (!allowed) {
      toast.error("Access denied: you cannot view this user's data.");
      return;
    }

    let cancelled = false;
    const preload = async () => {
      setTabLoading(true);
      const cfg = { headers: authHeaders() };
      const [inv, tsk, vip, rep] = await Promise.allSettled([
        axios.get(`${API_URL}/invoices/user/${userId}`, cfg),
        axios.get(`${API_URL}/tasks/user/${userId}`, cfg),
        axios.get(`${API_URL}/facility-vip/${userId}`, cfg),
        axios.get(`${API_URL}/reports/${userId}`, cfg),
      ]);
      if (cancelled) return;

      if (inv.status === "fulfilled") setInvoices(inv.value.data.invoices || []);
      else toast.error(inv.reason?.response?.data?.message || "Failed to fetch invoices.");

      if (tsk.status === "fulfilled") setTasks(tsk.value.data.tasks || []);
      else toast.error(tsk.reason?.response?.data?.message || "Failed to fetch tasks.");

      if (vip.status === "fulfilled") setFacilityVip(vip.value.data.data || null);
      else if (vip.reason?.response?.status !== 404) {
        toast.error(vip.reason?.response?.data?.message || "Failed to fetch FacilityVip data.");
      }

      if (rep.status === "fulfilled") setReports(rep.value.data.data || []);
      else toast.error(rep.reason?.response?.data?.message || "Failed to fetch reports.");

      setTabLoading(false);
    };
    preload();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUser, userRole, currentUserId]);

  /* Rechargements après une modification */
  const reloadInvoices = async () => {
    const res = await axios.get(`${API_URL}/invoices/user/${selectedUser._id}`, { headers: authHeaders() });
    setInvoices(res.data.invoices || []);
  };
  const reloadTasks = async () => {
    const res = await axios.get(`${API_URL}/tasks/user/${selectedUser._id}`, { headers: authHeaders() });
    setTasks(res.data.tasks || []);
  };
  const reloadFacilityVip = async () => {
    const res = await axios.get(`${API_URL}/facility-vip/${selectedUser._id}`, { headers: authHeaders() });
    setFacilityVip(res.data.data || null);
  };
  const reloadReports = async () => {
    const res = await axios.get(`${API_URL}/reports/${selectedUser._id}`, { headers: authHeaders() });
    setReports(res.data.data || []);
  };

  /* Demande de confirmation (remplace window.confirm) */
  const ask = (options) => setConfirm(options);
  const runConfirm = async () => {
    const current = confirm;
    setConfirm(null);
    if (current?.onConfirm) await current.onConfirm();
  };

  /* ---------- Ouverture d'un PDF dans un nouvel onglet ---------- */
  const openBlob = async (url, errorMessage) => {
    try {
      const response = await axios.get(url, { headers: authHeaders(), responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      window.open(blobUrl, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
    } catch (error) {
      console.error(errorMessage, error);
      toast.error(error.response?.data?.message || errorMessage);
    }
  };

  /* ---------- Formulaires ---------- */
  const closeForm = () => {
    setFormType(null);
    setEditInvoiceId(null);
    setEditingVip(false);
    setForm(emptyForm());
    setVipForm(emptyVip);
  };

  const openForm = (type) => {
    closeForm();
    setFormType(type);
  };

  const validatePdf = (file) => {
    if (!file) {
      toast.error("Please select a PDF file.");
      return false;
    }
    if (file.type !== "application/pdf") {
      toast.error("Only PDF files are allowed.");
      return false;
    }
    return true;
  };

  /* ---------- Factures ---------- */
  const handleViewInvoice = (id) =>
    openBlob(`${API_URL}/invoices/view/${id}`, "Unable to open the invoice PDF.");

  const handleDownloadInvoice = async (invoiceId) => {
    try {
      const response = await axios.get(`${API_URL}/invoices/download/${invoiceId}`, {
        headers: authHeaders(),
        responseType: "blob",
      });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `invoice-${invoiceId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Error downloading invoice PDF:", error);
      toast.error(error.response?.data?.message || "Unable to download the invoice.");
    }
  };

  const handleDeleteInvoice = (invoiceId) =>
    ask({
      title: "Delete this invoice?",
      message: "The invoice will be permanently deleted.",
      confirmLabel: "Delete",
      onConfirm: async () => {
        try {
          const response = await axios.delete(`${API_URL}/invoices/${invoiceId}`, { headers: authHeaders() });
          toast.success(response.data?.message || "Invoice deleted successfully.");
          await reloadInvoices();
        } catch (err) {
          console.error("Error deleting invoice:", err);
          toast.error(err.response?.data?.message || "Error deleting the invoice.");
        }
      },
    });

  const startEditInvoice = (invoice) => {
    setFormType("invoice");
    setEditInvoiceId(invoice._id);
    setForm({
      file: null,
      date: new Date(invoice.invoiceDate).toISOString().split("T")[0],
      status: invoice.status,
    });
  };

  /* Création / modification d'une facture (toujours "general") */
  const submitInvoice = async () => {
    if (!form.date) return toast.error("Please select a date.");
    const editing = Boolean(editInvoiceId);
    if (!editing && !validatePdf(form.file)) return;

    const formData = new FormData();
    formData.append("invoiceDate", form.date);
    formData.append("status", form.status);
    // En création uniquement : fichier + type "general"
    // (la route PUT du backend n'accepte pas de nouveau PDF)
    if (!editing) {
      formData.append("pdf", form.file);
      formData.append("invoiceType", "general");
    }

    setSaving(true);
    try {
      const config = {
        headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
      };
      if (editing) {
        await axios.put(`${API_URL}/invoices/${editInvoiceId}`, formData, config);
      } else {
        await axios.post(`${API_URL}/invoices/upload/${selectedUser._id}`, formData, config);
      }
      toast.success(editing ? "Invoice updated successfully!" : "Invoice uploaded successfully!");
      closeForm();
      await reloadInvoices();
    } catch (error) {
      console.error("Upload error:", error.response?.data || error.message);
      toast.error(
        error.response?.data?.message ||
          (editing ? "Failed to update invoice." : "Failed to upload invoice.")
      );
    } finally {
      setSaving(false);
    }
  };

  /* ---------- Tâches ---------- */
  const submitTask = async () => {
    if (!form.date) return toast.error("Please select a date.");
    if (!validatePdf(form.file)) return;

    const formData = new FormData();
    formData.append("pdf", form.file);
    formData.append("taskDate", form.date);
    formData.append("status", form.status);

    setSaving(true);
    try {
      await axios.post(`${API_URL}/tasks/upload/${selectedUser._id}`, formData, {
        headers: authHeaders(),
      });
      toast.success("Task uploaded successfully!");
      closeForm();
      await reloadTasks();
    } catch (error) {
      console.error("Task upload error:", error.response?.data || error.message);
      toast.error(error.response?.data?.message || "Failed to upload task.");
    } finally {
      setSaving(false);
    }
  };

  /* ---------- FacilityVip ---------- */
  const startEditVip = () => {
    setFormType("facilityVip");
    setEditingVip(true);
    setVipForm({
      totalPoints: String(facilityVip?.totalPoints ?? 0),
      availablePoints: String(facilityVip?.availablePoints ?? 0),
      BlockedPoints: String(facilityVip?.BlockedPoints ?? 0),
    });
  };

  const submitVip = async () => {
    const payload = {
      totalPoints: Number(vipForm.totalPoints) || 0,
      availablePoints: Number(vipForm.availablePoints) || 0,
      BlockedPoints: Number(vipForm.BlockedPoints) || 0,
    };
    if (Object.values(payload).some((v) => v < 0)) {
      return toast.error("Points cannot be negative.");
    }

    setSaving(true);
    try {
      const config = { headers: { ...authHeaders(), "Content-Type": "application/json" } };
      if (editingVip) {
        await axios.put(`${API_URL}/facility-vip/${selectedUser._id}`, payload, config);
      } else {
        await axios.post(`${API_URL}/facility-vip`, { userId: selectedUser._id, ...payload }, config);
      }
      toast.success(editingVip ? "FacilityVip updated successfully!" : "FacilityVip created successfully!");
      closeForm();
      await reloadFacilityVip();
    } catch (error) {
      console.error("FacilityVip error:", error.response?.data || error.message);
      toast.error(
        error.response?.data?.message ||
          (editingVip ? "Failed to update FacilityVip." : "Failed to create FacilityVip.")
      );
    } finally {
      setSaving(false);
    }
  };

  /* ---------- Rapports ---------- */
  const handleViewReport = (id) =>
    openBlob(`${API_URL}/reports/view/${id}`, "Unable to open the report PDF.");

  const handleDeleteReport = (reportId) =>
    ask({
      title: "Delete this report?",
      message: "The report will be permanently deleted.",
      confirmLabel: "Delete",
      onConfirm: async () => {
        try {
          await axios.delete(`${API_URL}/reports/${reportId}`, { headers: authHeaders() });
          setReports((prev) => prev.filter((r) => r._id !== reportId));
          toast.success("Report deleted successfully.");
        } catch (error) {
          console.error("Error during report deletion:", error);
          toast.error(error.response?.data?.message || "Failed to delete the report.");
        }
      },
    });

  const submitReport = async () => {
    if (!validatePdf(form.file)) return;

    const formData = new FormData();
    formData.append("pdf", form.file);
    formData.append("userId", selectedUser._id);
    formData.append("modelId", "example-model-id"); // Remplacez par un modelId réel si nécessaire

    setSaving(true);
    try {
      await axios.post(`${API_URL}/reports`, formData, {
        headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
      });
      toast.success("Report uploaded successfully!");
      closeForm();
      await reloadReports();
    } catch (error) {
      console.error("Report upload error:", error.response?.data || error.message);
      toast.error(error.response?.data?.message || "Failed to upload report.");
    } finally {
      setSaving(false);
    }
  };

  /* ---------- Block / Unblock (admin uniquement) ---------- */
  const toggleBlock = async () => {
    try {
      await axios.put(`${API_URL}/users/${selectedUser._id}/block`, {}, { headers: authHeaders() });
      setProfile((prev) => ({ ...prev, isBlocked: !prev.isBlocked }));
      toast.success(profile?.isBlocked ? "User unblocked" : "User blocked");
    } catch (error) {
      console.error("Error toggling block status:", error.response?.data || error.message);
      toast.error("Failed to update block status.");
    }
  };

  const handleBlockClick = () => {
    if (profile?.isBlocked) return toggleBlock();
    ask({
      title: "Block this user?",
      message: "The user will lose access until you unblock them.",
      confirmLabel: "Block user",
      onConfirm: toggleBlock,
    });
  };

  /* ---------- Navigation ---------- */
  const resetUserData = () => {
    setProfile(null);
    setInvoices([]);
    setTasks([]);
    setFacilityVip(null);
    setReports([]);
    setFilterMonth("");
    setFilterYear("");
    setViewMode("details");
    closeForm();
  };

  const handleBack = () => {
    resetUserData();
    setSelectedUser(null);
  };

  const goToUser = (user) => {
    if (!user || user._id === selectedUser?._id) return;
    resetUserData();
    setSelectedUser(user);
  };

  const hasInnerBack = Boolean(formType);

  /* Touche Échap : remonte d'un niveau à la fois, puis ferme le popup */
  const handleEscape = () => {
    if (confirm) return setConfirm(null);
    if (!selectedUser) return onClose?.();
    if (formType) return closeForm();
    handleBack();
  };

  /* ---------- Valeurs dérivées ---------- */
  const filteredUsers = users
    .filter((user) => {
      const search = searchTerm.trim().toLowerCase();
      const haystack = `${user.name || ""} ${user.surname || ""} ${user.email || ""}`.toLowerCase();
      return (
        haystack.includes(search) &&
        (isCommercial ||
          filter === "all" ||
          (filter === "verified" && user.isVerified) ||
          (filter === "notVerified" && !user.isVerified))
      );
    })
    .sort((a, b) =>
      `${a.name || ""} ${a.surname || ""}`.localeCompare(`${b.name || ""} ${b.surname || ""}`)
    );

  const filterCounts = {
    all: users.length,
    verified: users.filter((u) => u.isVerified).length,
    notVerified: users.filter((u) => !u.isVerified).length,
  };

  /* Utilisateurs parmi lesquels on peut passer d'un profil à l'autre */
  const navUsers = (
    selectedUser && filteredUsers.some((u) => u._id === selectedUser._id) ? filteredUsers : users
  ).filter((u) => u.isVerified);
  const userIndex = selectedUser ? navUsers.findIndex((u) => u._id === selectedUser._id) : -1;

  /* Uniquement les factures générales (les anciennes sans type sont "general") */
  const generalInvoices = invoices.filter((inv) => (inv.invoiceType || "general") === "general");

  const visibleInvoices = generalInvoices.filter((invoice) => {
    const d = new Date(invoice.invoiceDate);
    const matchMonth = filterMonth === "" || d.getMonth().toString() === filterMonth;
    const matchYear = filterYear === "" || d.getFullYear().toString() === filterYear;
    return matchMonth && matchYear;
  });

  const counts = {
    invoices: generalInvoices.length,
    tasks: tasks.length,
    reports: reports.length,
  };

  const summaryTiles = [
    ["invoices", tabLoading ? "…" : generalInvoices.length, "Invoices"],
    ["tasks", tabLoading ? "…" : tasks.length, "Tasks"],
    ["reports", tabLoading ? "…" : reports.length, "Reports"],
    [
      "facilityVip",
      tabLoading ? "…" : facilityVip ? Number(facilityVip.availablePoints || 0).toLocaleString() : "—",
      "Available points",
    ],
  ];

  const blockButton = !isCommercial && (
    <button
      className={`adm-btn block ${profile?.isBlocked ? "success" : "danger"}`}
      onClick={handleBlockClick}
      disabled={!profile}
      title={profile?.isBlocked ? "Unblock user" : "Block user"}
    >
      {profile?.isBlocked ? <CheckCircle /> : <XCircle />}
      <span className="adm-btn-text">{profile?.isBlocked ? "Unblock user" : "Block user"}</span>
    </button>
  );

  const confirmDialog = (
    <ConfirmDialog
      open={Boolean(confirm)}
      title={confirm?.title}
      message={confirm?.message}
      confirmLabel={confirm?.confirmLabel}
      onConfirm={runConfirm}
      onCancel={() => setConfirm(null)}
    />
  );

  /* ============================================================
     Rendu : écran 1 → liste des utilisateurs
     ============================================================ */
  if (!selectedUser) {
    return (
      <AdminPopup onClose={onClose} onEscape={handleEscape}>
        <div className="adm-listview">
          <div className="adm-topbar">
            <button className="adm-back" onClick={onClose} aria-label="Close">
              <ArrowLeft /> Back
            </button>
            <h3>Users</h3>
            <span className="adm-topbar-count">{filterCounts.all} total</span>
          </div>

          {/* Zone fixe : recherche + filtres */}
          <div className="adm-list-tools">
            <div className="adm-searchbox">
              <Search />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
              />
              {searchTerm && (
                <button
                  className="adm-searchbox-clear"
                  onClick={() => setSearchTerm("")}
                  aria-label="Clear search"
                >
                  <X />
                </button>
              )}
            </div>

            {/* L'admin commercial ne voit que les utilisateurs vérifiés : pas de filtres */}
            {!isCommercial && (
              <div className="adm-filters">
                {[
                  ["all", "All"],
                  ["verified", "Verified"],
                  ["notVerified", "Not verified"],
                ].map(([key, label]) => (
                  <button
                    key={key}
                    className={`adm-chip ${filter === key ? "active" : ""}`}
                    onClick={() => setFilter(key)}
                  >
                    {label}
                    <span className="adm-chip-count">{filterCounts[key]}</span>
                  </button>
                ))}
              </div>
            )}
            {isCommercial && <div style={{ height: 12 }} />}
          </div>

          {/* Zone qui défile */}
          <div className="adm-body">
            {isLoading ? (
              <Skeleton rows={5} />
            ) : loadError ? (
              <Empty>{loadError}</Empty>
            ) : filteredUsers.length > 0 ? (
              <div className="adm-table">
                <div className="adm-table-head">
                  <span>User</span>
                  <span>Status</span>
                </div>
                {filteredUsers.map((user) => (
                  <div
                    key={user._id}
                    className={`adm-urow ${user.isVerified ? "clickable" : "locked"}`}
                    onClick={() => {
                      if (user.isVerified) {
                        setSelectedUser(user);
                        setViewMode("details");
                      }
                    }}
                    title={user.isVerified ? "" : "Verify this user to open the profile"}
                  >
                    <Avatar user={user} />
                    <div className="adm-urow-text">
                      <div className="adm-urow-name">
                        {user.name} {user.surname}
                      </div>
                      <div className="adm-urow-sub">{user.email || "No email"}</div>
                    </div>
                    <Pill tone={user.isVerified ? "ok" : "bad"}>
                      {user.isVerified ? "Verified" : "Not verified"}
                    </Pill>
                    {user.isVerified ? (
                      <ChevronRight className="adm-urow-arrow" />
                    ) : (
                      <Lock className="adm-urow-arrow" />
                    )}
                  </div>
                ))}
                <div className="adm-table-foot">
                  {filteredUsers.length} user{filteredUsers.length === 1 ? "" : "s"}
                </div>
              </div>
            ) : (
              <Empty>
                <Search size={20} />
                No users match your search.
              </Empty>
            )}
          </div>
        </div>
      </AdminPopup>
    );
  }

  /* ============================================================
     Rendu : écran 2 → sidebar + contenu
     ============================================================ */
  const side = (
    <>
      <button className="adm-back" onClick={handleBack} title="All users">
        <ArrowLeft />
        <span className="adm-hide-collapsed">All users</span>
      </button>

      <div className="adm-user">
        <Avatar user={profile || selectedUser} large />
        <h4 className="adm-user-name">
          {profile?.name || selectedUser.name} {profile?.surname || selectedUser.surname}
        </h4>
        <Pill tone={profile?.isBlocked ? "bad" : "ok"}>{profile?.isBlocked ? "Blocked" : "Active"}</Pill>

        {navUsers.length > 1 && userIndex >= 0 && (
          <div className="adm-user-switch">
            <button
              className="adm-icon-btn sm"
              title="Previous user"
              aria-label="Previous user"
              onClick={() => goToUser(navUsers[(userIndex - 1 + navUsers.length) % navUsers.length])}
            >
              <ChevronLeft />
            </button>
            <span>
              {userIndex + 1} / {navUsers.length}
            </span>
            <button
              className="adm-icon-btn sm"
              title="Next user"
              aria-label="Next user"
              onClick={() => goToUser(navUsers[(userIndex + 1) % navUsers.length])}
            >
              <ChevronRight />
            </button>
          </div>
        )}
      </div>

      <SideNav
        groups={NAV_GROUPS}
        active={viewMode}
        counts={counts}
        collapsed={collapsed}
        onSelect={(key) => {
          setViewMode(key);
          closeForm();
        }}
      />

      <div className="adm-side-foot">
        <CollapseButton collapsed={collapsed} onToggle={toggleCollapsed} />
        {blockButton}
      </div>
    </>
  );

  /* ---------- Formulaires ---------- */
  const renderForm = () => {
    if (formType === "invoice") {
      const editing = Boolean(editInvoiceId);
      return (
        <FormCard
          title={editing ? "Edit invoice" : "New invoice"}
          submitLabel={editing ? "Update" : "Upload"}
          onSubmit={submitInvoice}
          onCancel={closeForm}
          busy={saving}
          disabled={!form.date || (!editing && !form.file)}
        >
          {!editing && (
            <FileInput file={form.file} setFile={(f) => setForm({ ...form, file: f })} disabled={saving} />
          )}
          <label>
            <span>Invoice date</span>
            <input
              type="date"
              value={form.date}
              disabled={saving}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
            />
          </label>
          <label>
            <span>Status</span>
            <select
              value={form.status}
              disabled={saving}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
            >
              <option value="pending">Pending</option>
              <option value="paid">Paid</option>
            </select>
          </label>
        </FormCard>
      );
    }

    if (formType === "task") {
      return (
        <FormCard
          title="New task"
          submitLabel="Upload"
          onSubmit={submitTask}
          onCancel={closeForm}
          busy={saving}
          disabled={!form.date || !form.file}
        >
          <FileInput file={form.file} setFile={(f) => setForm({ ...form, file: f })} disabled={saving} />
          <label>
            <span>Task date</span>
            <input
              type="date"
              value={form.date}
              disabled={saving}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
            />
          </label>
        </FormCard>
      );
    }

    if (formType === "facilityVip") {
      return (
        <FormCard
          title={editingVip ? "Edit FacilityVip" : "New FacilityVip"}
          submitLabel={editingVip ? "Update" : "Create"}
          onSubmit={submitVip}
          onCancel={closeForm}
          busy={saving}
        >
          {[
            ["totalPoints", "Total points"],
            ["availablePoints", "Available points"],
            ["BlockedPoints", "Blocked points"],
          ].map(([key, label]) => (
            <label key={key}>
              <span>{label}</span>
              <input
                type="number"
                min="0"
                className="adm-search"
                value={vipForm[key]}
                disabled={saving}
                onChange={(e) => setVipForm({ ...vipForm, [key]: e.target.value })}
              />
            </label>
          ))}
        </FormCard>
      );
    }

    if (formType === "report") {
      return (
        <FormCard
          title="New report"
          submitLabel="Upload"
          onSubmit={submitReport}
          onCancel={closeForm}
          busy={saving}
          disabled={!form.file}
        >
          <FileInput file={form.file} setFile={(f) => setForm({ ...form, file: f })} disabled={saving} />
        </FormCard>
      );
    }

    return null;
  };

  /* ---------- Contenu des onglets ---------- */
  const content = formType ? (
    renderForm()
  ) : viewMode === "details" ? (
    profileLoading ? (
      <Skeleton rows={5} />
    ) : (
      <>
        <div className="adm-summary">
          {summaryTiles.map(([target, value, label]) => (
            <button key={label} className="adm-tile" onClick={() => setViewMode(target)}>
              <div className="adm-tile-value">{value}</div>
              <div className="adm-tile-label">{label}</div>
            </button>
          ))}
        </div>

        <div className="adm-fields">
          <InfoField icon={Mail} label="Email" value={profile?.email} />
          <InfoField icon={MapPin} label="Address" value={profile?.address} />
          <InfoField icon={Phone} label="Phone" value={profile?.mobile || profile?.phoneNumber} />
          <InfoField
            icon={Calendar}
            label="Date of birth"
            value={profile?.dateOfBirth && formatDate(profile.dateOfBirth)}
          />
          <InfoField icon={Globe} label="Country" value={profile?.country} />
        </div>

        {/* Sur mobile la sidebar n'a pas de pied : le bouton est ici (admin uniquement) */}
        {blockButton && <div className="adm-danger-zone adm-mobile-only">{blockButton}</div>}
      </>
    )
  ) : tabLoading ? (
    <Skeleton rows={4} />
  ) : viewMode === "invoices" ? (
    <>
      <div className="adm-doc-toolbar">
        <h5>Invoices</h5>
        <button className="adm-btn primary" onClick={() => openForm("invoice")}>
          <Plus /> Add
        </button>
      </div>

      <div className="cm-filters">
        <select
          className="cm-select"
          value={filterMonth}
          onChange={(e) => setFilterMonth(e.target.value)}
          aria-label="Filter by month"
        >
          <option value="">All months</option>
          {MONTHS.map((m, i) => (
            <option key={m} value={i}>
              {m}
            </option>
          ))}
        </select>
        <select
          className="cm-select"
          value={filterYear}
          onChange={(e) => setFilterYear(e.target.value)}
          aria-label="Filter by year"
        >
          <option value="">All years</option>
          {Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i).map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>
        {(filterMonth || filterYear) && (
          <button
            className="adm-icon-btn"
            title="Clear filters"
            aria-label="Clear filters"
            onClick={() => {
              setFilterMonth("");
              setFilterYear("");
            }}
          >
            <X />
          </button>
        )}
      </div>

      {visibleInvoices.length === 0 ? (
        <Empty>
          {generalInvoices.length > 0
            ? "No invoices found for this filter."
            : "No invoices yet. Add the first one."}
        </Empty>
      ) : (
        visibleInvoices.map((inv) => (
          <div key={inv._id} className="adm-doc">
            <div className="adm-doc-icon">
              <FileText size={18} />
            </div>
            <div className="adm-doc-text">
              <div className="adm-doc-title">Invoice · {formatDate(inv.invoiceDate)}</div>
              <div className="adm-doc-date">
                <StatusBadge status={inv.status} />
              </div>
            </div>
            <button className="adm-icon-btn" title="View invoice" onClick={() => handleViewInvoice(inv._id)}>
              <Eye />
            </button>
            <button className="adm-icon-btn" title="Download" onClick={() => handleDownloadInvoice(inv._id)}>
              <Download />
            </button>
            <button className="adm-icon-btn" title="Edit" onClick={() => startEditInvoice(inv)}>
              <Edit />
            </button>
            <button
              className="adm-icon-btn danger"
              title="Delete"
              onClick={() => handleDeleteInvoice(inv._id)}
            >
              <Trash2 />
            </button>
          </div>
        ))
      )}
    </>
  ) : viewMode === "tasks" ? (
    <>
      <div className="adm-doc-toolbar">
        <h5>Tasks</h5>
        <button className="adm-btn primary" onClick={() => openForm("task")}>
          <Plus /> Add
        </button>
      </div>
      {tasks.length > 0 ? (
        <PopupTasks userId={selectedUser._id} tasks={tasks} onTasksUpdate={setTasks} />
      ) : (
        <Empty>No tasks yet. Add the first one.</Empty>
      )}
    </>
  ) : viewMode === "facilityVip" ? (
    <>
      <div className="adm-doc-toolbar">
        <h5>Points</h5>
        {facilityVip ? (
          <button className="adm-btn primary" onClick={startEditVip}>
            <Edit /> Edit
          </button>
        ) : (
          <button className="adm-btn primary" onClick={() => openForm("facilityVip")}>
            <Plus /> Add FacilityVip
          </button>
        )}
      </div>
      {facilityVip ? (
        <div className="cm-stats">
          <div className="cm-stat">
            <div className="cm-stat-value">{Number(facilityVip.totalPoints || 0).toLocaleString()}</div>
            <div className="cm-stat-label">Total points</div>
          </div>
          <div className="cm-stat ok">
            <div className="cm-stat-value">{Number(facilityVip.availablePoints || 0).toLocaleString()}</div>
            <div className="cm-stat-label">Available</div>
          </div>
          <div className="cm-stat warn">
            <div className="cm-stat-value">{Number(facilityVip.BlockedPoints || 0).toLocaleString()}</div>
            <div className="cm-stat-label">Blocked</div>
          </div>
        </div>
      ) : (
        <Empty>No FacilityVip data found.</Empty>
      )}
    </>
  ) : viewMode === "reports" ? (
    <DocsPanel
      title="Reports"
      itemLabel="Report"
      items={reports}
      onAdd={() => openForm("report")}
      onView={handleViewReport}
      onDelete={handleDeleteReport}
      emptyText="No reports yet. Add the first one."
    />
  ) : null;

  return (
    <AdminPopup onClose={onClose} onEscape={handleEscape}>
      <AdminLayout
        collapsed={collapsed}
        side={side}
        title={TAB_TITLES[viewMode]}
        headStart={
          hasInnerBack && (
            <button className="adm-icon-btn" onClick={closeForm} title="Back" aria-label="Back">
              <ArrowLeft />
            </button>
          )
        }
      >
        {content}
      </AdminLayout>
      {confirmDialog}
    </AdminPopup>
  );
};

export default Profile;